import type { JSX, ReactNode } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnyAction, Dispatch } from 'redux';
import styled from 'styled-components';

import { ErrorIcon, ErrorValidation } from '../ErrorValidation';
import InputProvider from '../InputProvider';
import Label from '../Label';
import LabelWrapper from '../LabelWrapper';
import RequireIndicator from '../RequireIndicator';

const MainWrapper = styled.div`
  width: 100%;
`;

const InputWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px;
  padding-right: 30px;
  border: 1px solid
    ${({ hasError }) =>
      hasError
        ? 'var(--require-border)'
        : 'var(--input-border-normal)'};
`;

const ToggleText = styled.span`
  position: absolute;
  bottom: 1px;
  right: 20px;
  width: 45px;
  height: 24px;
  font-size: 12px;
  text-align: right;

  &:hover {
    cursor: pointer;
  }
`;

const Footer = styled.div`
  margin: 3px 0;
  font-size: 12px;
  font-weight: lighter;

  a {
    color: var(--cargill-green);
  }
`;

interface UnknownRecord {
  [key: string]: unknown;
}

interface FormikLike {
  field: {
    value: unknown;
  };
  form: {
    setFieldTouched: (name: string, touched: boolean) => Promise<UnknownRecord>;
    setFieldValue: (name: string, value: unknown, shouldValidate?: boolean) => void;
    errors: UnknownRecord;
    touched: UnknownRecord;
  };
}

interface FieldMaskedInputProps {
  formik: FormikLike;
  store: { customer: UnknownRecord };
  name: string;
  required?: boolean;
  label?: string;
  footer?: ReactNode;
  placeholder?: string;
  hasGroupError?: boolean;
  dispatch: Dispatch<AnyAction>;
  apiService: {
    updateCustomer: (
      customer: UnknownRecord,
      dispatch: Dispatch<AnyAction>
    ) => void;
  };
  onChange?: (event: unknown) => void;
}

function FieldMaskedInput({
  formik,
  store,
  name,
  required,
  label,
  footer,
  placeholder,
  hasGroupError,
  dispatch,
  apiService,
  onChange
}: FieldMaskedInputProps): JSX.Element {
  const { t } = useTranslation();
  const [shouldShowInput, setShouldShowInput] = useState(false);

  return (
    <InputProvider
      formik={formik}
      name={name}
      dispatch={dispatch}
      store={store}
      apiService={apiService}
      onChange={onChange}
    >
      {(args) => {
        const {
          hasError,
          isTouched,
          handleOnBlur,
          handleOnChange,
          errorMessage
        } = args as {
          hasError: boolean;
          isTouched: boolean;
          handleOnBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
          handleOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
          errorMessage?: ReactNode;
        };

        return (
          <MainWrapper>
            <InputWrapper>
              {label && (
                <LabelWrapper>
                  {required && <RequireIndicator />}
                  <Label>{label}</Label>
                </LabelWrapper>
              )}

              <Input
                type={shouldShowInput ? 'text' : 'password'}
                name={name}
                hasError={(hasError && isTouched) || hasGroupError}
                onBlur={handleOnBlur}
                value={(formik.field.value as string) || ''}
                placeholder={placeholder}
                onChange={handleOnChange}
              />

              <ToggleText onClick={() => setShouldShowInput(v => !v)}>
                {shouldShowInput
                  ? t('buttons.hide')
                  : t('buttons.show')}
              </ToggleText>
            </InputWrapper>

            {hasError && isTouched && (
              <ErrorValidation>
                <ErrorIcon />
                <p>{errorMessage}</p>
              </ErrorValidation>
            )}

            {footer && <Footer>{footer}</Footer>}
          </MainWrapper>
        );
      }}
    </InputProvider>
  );
}

export default FieldMaskedInput;
